import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Lock } from "lucide-react";

const MATTER_TYPES = ["conveyancing", "antenuptial", "trust"];
const SURCHARGE_KEYS = ["sectional_title", "trust_buyer", "company_buyer", "high_value"];

export default function AdminPricing() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/pricing-config");
        setCfg(data);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="p-10 text-zinc-500">Loading pricing config…</div>;
  if (!cfg)    return <div className="p-10 text-red-700">No pricing config available</div>;

  const setBase = (k, v) => setCfg({ ...cfg, base_legal_fees: { ...cfg.base_legal_fees, [k]: Number(v) } });
  const setSur  = (k, v) => setCfg({ ...cfg, surcharges: { ...cfg.surcharges, [k]: Number(v) } });

  const save = async () => {
    if (!isAdmin) {
      toast.error("Only admins can update pricing config");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/pricing-config", {
        base_legal_fees: cfg.base_legal_fees,
        admin_component: Number(cfg.admin_component),
        surcharges: cfg.surcharges,
        assisted_discount_pct: Number(cfg.assisted_discount_pct),
        vat_pct: Number(cfg.vat_pct),
        estimate_spread_pct: Number(cfg.estimate_spread_pct),
      });
      setCfg(data);
      toast.success(`Saved — version ${data.version}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-10 max-w-5xl mx-auto" data-testid="admin-pricing">
      <div className="mb-10">
        <div className="text-overline text-zinc-500">Pricing</div>
        <h1 className="font-serif text-4xl lg:text-5xl font-medium tracking-tight mt-2">Firm pricing engine</h1>
        <p className="mt-2 text-sm text-zinc-600">
          Edit the deterministic pricing engine that feeds the calculator and pro-formas.
          Config version: <b>v{cfg.version}</b>.
        </p>
        {!isAdmin && (
          <div className="mt-4 flex items-center gap-2 bg-amber-50 border border-amber-200 px-4 py-2 text-xs text-amber-800" data-testid="readonly-banner">
            <Lock className="h-3 w-3" /> Read-only — only admin role can save changes.
          </div>
        )}
      </div>

      <div className="bg-white border border-zinc-200 p-8 space-y-8">
        <Section title="Base legal fees per matter type (ZAR)">
          <div className="grid sm:grid-cols-3 gap-4">
            {MATTER_TYPES.map((m) => (
              <FieldZAR key={m} label={m} value={cfg.base_legal_fees?.[m] ?? 0} onChange={(v) => setBase(m, v)} testid={`pricing-base-${m}`} />
            ))}
          </div>
        </Section>

        <Section title="Admin component & VAT">
          <div className="grid sm:grid-cols-3 gap-4">
            <FieldZAR label="Admin component"            value={cfg.admin_component}        onChange={(v) => setCfg({ ...cfg, admin_component: v })} testid="pricing-admin-component" />
            <FieldPct label="VAT %"                      value={cfg.vat_pct}                onChange={(v) => setCfg({ ...cfg, vat_pct: v })}         testid="pricing-vat" />
            <FieldPct label="Assisted discount %"        value={cfg.assisted_discount_pct}  onChange={(v) => setCfg({ ...cfg, assisted_discount_pct: v })} testid="pricing-assisted" />
          </div>
        </Section>

        <Section title="Estimate spread (min/max ±%)">
          <FieldPct label="Spread %" value={cfg.estimate_spread_pct} onChange={(v) => setCfg({ ...cfg, estimate_spread_pct: v })} testid="pricing-spread" />
          <p className="text-xs text-zinc-500 mt-2">
            Applied to the legal/admin portion only — SARS transfer duty and Deeds Office fees are exact.
          </p>
        </Section>

        <Section title="Surcharges (ZAR)">
          <div className="grid sm:grid-cols-2 gap-4">
            {SURCHARGE_KEYS.map((k) => (
              <FieldZAR key={k} label={k.replace(/_/g, " ")} value={cfg.surcharges?.[k] ?? 0} onChange={(v) => setSur(k, v)} testid={`pricing-surcharge-${k}`} />
            ))}
          </div>
        </Section>

        <Section title="Regulatory source versions (placeholder for future integration)">
          <div className="grid sm:grid-cols-3 gap-4 text-sm">
            {Object.entries(cfg.regulatory_source_versions || {}).map(([k, v]) => (
              <div key={k} className="border border-zinc-200 p-3 bg-zinc-50">
                <div className="text-overline text-zinc-500">{k.replace(/_/g, " ")}</div>
                <div className="font-mono text-sm mt-1">{v}</div>
              </div>
            ))}
          </div>
        </Section>

        <div className="flex justify-end pt-4 border-t border-zinc-100">
          <Button onClick={save} disabled={!isAdmin || saving} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="pricing-save">
            {saving ? "Saving…" : "Save pricing config"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-overline text-zinc-500 mb-3">{title}</div>
      {children}
    </div>
  );
}

function FieldZAR({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="capitalize">{label}</Label>
      <div className="relative mt-1">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-xs">R</span>
        <Input
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ""))}
          className="pl-6 rounded-none font-mono"
          data-testid={testid}
        />
      </div>
    </div>
  );
}

function FieldPct({ label, value, onChange, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ""))}
        className="mt-1 rounded-none font-mono"
        data-testid={testid}
      />
    </div>
  );
}
